// SPDX-License-Identifier: AGPL-3.0-or-later

import type {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {getParserFlagsForContext} from '@app/features/messaging/utils/markdown/MarkdownParserFlags';
import {NodeType} from '@app/features/messaging/utils/markdown/parser/Enums';
import {parseMarkdownAstWithWasm} from '@app/features/messaging/utils/markdown/parser/MarkdownParserWasm';
import type {
	AccordionNode,
	AlertNode,
	BlockquoteNode,
	DisplayMathNode,
	FormattingNode,
	HeadingNode,
	InlineMathNode,
	ListNode,
	Node,
	SubtextNode,
	TableNode,
	TextNode,
} from '@app/features/messaging/utils/markdown/parser/Nodes';

const INLINE_MATH_REGEX = /(?<!\\|\$)\$(?!\s)([^$\n]+?)(?<!\s)\$(?!\$)/g;
const DISPLAY_MATH_REGEX = /\$\$([\s\S]+?)\$\$/g;
const DETAILS_REGEX = /<details(?:\s+(open))?>\s*<summary>([\s\S]*?)<\/summary>([\s\S]*?)<\/details>/i;
const INLINE_ACCORDION_REGEX = />!\s*([^|\n]+?)\s*\|\s*([\s\S]+?)\s*!/i;
const MULTILINE_TEXT_ACCORDION_REGEX = /^>!\s*([^\n|]+?)\n([\s\S]+?)(?:$|\n\n)/m;

function isMathFormula(str: string): boolean {
	const trimmed = str.trim();
	if (!trimmed) return false;
	// Currency / number guard: e.g. "10", "19.99", "1,000", "50k", "100M"
	if (/^[\d,.]+(?:\s*[kKmMbB])?$/.test(trimmed)) {
		return false;
	}
	// LaTeX control sequences: \frac, \sqrt, \alpha, \sum, \int, etc.
	if (/\\(?:[a-zA-Z]+|.)/.test(trimmed)) {
		return true;
	}
	// Math operators with algebraic variables: +, -, *, /, =, <, >, ^, _
	if (/[=+\-*/^_<>~]/.test(trimmed) && /[a-zA-Z]/.test(trimmed)) {
		return true;
	}
	// Greek letters or superscripts/subscripts
	if (/[α-ωΑ-Ω\u00B2\u00B3\u00B9\u2070-\u209F]/.test(trimmed)) {
		return true;
	}
	// Single variable letters like $x$, $y$, $n$
	if (/^[a-zA-Z]$/.test(trimmed)) {
		return true;
	}
	return false;
}

function parseInnerMarkdown(content: string, context?: MarkdownContext): Array<Node> {
	if (!content.trim()) return [];
	try {
		const flags = context !== undefined ? getParserFlagsForContext(context) : 0;
		const {nodes} = parseMarkdownAstWithWasm(content, flags);
		return nodes;
	} catch {
		return [{type: NodeType.Text, content}];
	}
}

function splitTextNodeForMath(node: TextNode): Array<Node> {
	const text = node.content;
	if (!text.includes('$')) {
		return [node];
	}

	// 1. Process Display Math ($$...$$) first
	if (text.includes('$$')) {
		DISPLAY_MATH_REGEX.lastIndex = 0;
		const match = DISPLAY_MATH_REGEX.exec(text);
		if (match) {
			const before = text.slice(0, match.index);
			const mathContent = match[1].trim();
			const after = text.slice(match.index + match[0].length);
			const result: Array<Node> = [];
			if (before) {
				result.push(...splitTextNodeForMath({type: NodeType.Text, content: before}));
			}
			result.push({type: NodeType.DisplayMath, content: mathContent} as DisplayMathNode);
			if (after) {
				result.push(...splitTextNodeForMath({type: NodeType.Text, content: after}));
			}
			return result;
		}
	}

	// 2. Process Inline Math ($...$)
	INLINE_MATH_REGEX.lastIndex = 0;
	let inlineMatch: RegExpExecArray | null = null;
	while ((inlineMatch = INLINE_MATH_REGEX.exec(text)) !== null) {
		const formula = inlineMatch[1];
		if (isMathFormula(formula)) {
			const before = text.slice(0, inlineMatch.index);
			const after = text.slice(inlineMatch.index + inlineMatch[0].length);
			const result: Array<Node> = [];
			if (before) {
				result.push(...splitTextNodeForMath({type: NodeType.Text, content: before}));
			}
			result.push({type: NodeType.InlineMath, content: formula.trim()} as InlineMathNode);
			if (after) {
				result.push(...splitTextNodeForMath({type: NodeType.Text, content: after}));
			}
			return result;
		}
	}

	return [node];
}

function splitTextNodeForAccordions(node: TextNode, context?: MarkdownContext, depth = 0): Array<Node> {
	const text = node.content;

	// Check <details><summary>Title</summary>Content</details>
	const detailsMatch = DETAILS_REGEX.exec(text);
	if (detailsMatch) {
		const before = text.slice(0, detailsMatch.index);
		const isOpen = Boolean(detailsMatch[1]);
		const title = detailsMatch[2].trim() || 'Details';
		const rawContent = detailsMatch[3].trim();
		const after = text.slice(detailsMatch.index + detailsMatch[0].length);

		const result: Array<Node> = [];
		if (before) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: before}, context, depth));
		}

		const innerChildren = depth < 3
			? enhanceSuperchargedMarkdown(parseInnerMarkdown(rawContent, context), context, depth + 1)
			: [{type: NodeType.Text, content: rawContent}];

		result.push({
			type: NodeType.Accordion,
			title,
			children: innerChildren,
			isOpenByDefault: isOpen,
		} as AccordionNode);

		if (after) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: after}, context, depth));
		}
		return result;
	}

	// Check >! Title | Content !<
	const accordionMatch = INLINE_ACCORDION_REGEX.exec(text);
	if (accordionMatch) {
		const before = text.slice(0, accordionMatch.index);
		const title = accordionMatch[1].trim() || 'Details';
		const rawContent = accordionMatch[2].trim();
		const after = text.slice(accordionMatch.index + accordionMatch[0].length);

		const result: Array<Node> = [];
		if (before) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: before}, context, depth));
		}

		const innerChildren = depth < 3
			? enhanceSuperchargedMarkdown(parseInnerMarkdown(rawContent, context), context, depth + 1)
			: [{type: NodeType.Text, content: rawContent}];

		result.push({
			type: NodeType.Accordion,
			title,
			children: innerChildren,
			isOpenByDefault: false,
		} as AccordionNode);

		if (after) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: after}, context, depth));
		}
		return result;
	}

	// Check multiline text accordion: >! Title\n> Content or >! Title\nContent
	const multilineMatch = MULTILINE_TEXT_ACCORDION_REGEX.exec(text);
	if (multilineMatch) {
		const before = text.slice(0, multilineMatch.index);
		const title = multilineMatch[1].trim() || 'Details';
		const rawContent = multilineMatch[2].replace(/^>\s?/gm, '').trim();
		const after = text.slice(multilineMatch.index + multilineMatch[0].length);

		const result: Array<Node> = [];
		if (before) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: before}, context, depth));
		}

		const innerChildren = depth < 3
			? enhanceSuperchargedMarkdown(parseInnerMarkdown(rawContent, context), context, depth + 1)
			: [{type: NodeType.Text, content: rawContent}];

		result.push({
			type: NodeType.Accordion,
			title,
			children: innerChildren,
			isOpenByDefault: false,
		} as AccordionNode);

		if (after) {
			result.push(...splitTextNodeForAccordions({type: NodeType.Text, content: after}, context, depth));
		}
		return result;
	}

	return [node];
}

function processBlockquoteForAccordion(node: BlockquoteNode, context?: MarkdownContext, depth = 0): Node {
	const children = node.children;
	if (children.length === 0) return node;

	const firstChild = children[0];
	if (firstChild.type === NodeType.Text && firstChild.content.startsWith('!')) {
		// Found >! blockquote accordion
		const rawFirstLine = firstChild.content.slice(1).trimStart();
		let title = 'Details';
		let innerTextNodes: Array<Node> = [];

		if (rawFirstLine.includes('|')) {
			const pipeIdx = rawFirstLine.indexOf('|');
			title = rawFirstLine.slice(0, pipeIdx).trim() || 'Details';
			const afterPipe = rawFirstLine.slice(pipeIdx + 1).replace(/!<$/, '').trim();
			if (afterPipe) {
				innerTextNodes = [{type: NodeType.Text, content: afterPipe}];
			}
		} else if (rawFirstLine.includes('\n')) {
			const newlineIdx = rawFirstLine.indexOf('\n');
			title = rawFirstLine.slice(0, newlineIdx).trim() || 'Details';
			const remainingLines = rawFirstLine.slice(newlineIdx + 1).trim();
			if (remainingLines) {
				innerTextNodes = [{type: NodeType.Text, content: remainingLines}];
			}
		} else {
			title = rawFirstLine.replace(/!<$/, '').trim() || 'Details';
		}

		const allChildren = [...innerTextNodes, ...children.slice(1)];
		const enhancedChildren = depth < 3
			? enhanceSuperchargedMarkdown(allChildren, context, depth + 1)
			: allChildren;

		return {
			type: NodeType.Accordion,
			title,
			children: enhancedChildren,
			isOpenByDefault: false,
		} as AccordionNode;
	}

	return {
		...node,
		children: enhanceSuperchargedMarkdown(node.children, context, depth),
	};
}

export function enhanceSuperchargedMarkdown(
	nodes: Array<Node>,
	context?: MarkdownContext,
	depth = 0,
): Array<Node> {
	if (!Array.isArray(nodes) || nodes.length === 0) {
		return nodes;
	}

	const result: Array<Node> = [];

	for (let i = 0; i < nodes.length; i++) {
		const node = nodes[i];

		// Lookahead: check if current node is header text ">! Title" and next node is a blockquote
		if (
			node.type === NodeType.Text &&
			i + 1 < nodes.length &&
			nodes[i + 1].type === NodeType.Blockquote
		) {
			const text = (node as TextNode).content.trim();
			const headerMatch = /^>!\s*([^\n|]+)$/.exec(text);
			if (headerMatch) {
				const title = headerMatch[1].trim() || 'Details';
				const nextBlockquote = nodes[i + 1] as BlockquoteNode;
				result.push({
					type: NodeType.Accordion,
					title,
					children: enhanceSuperchargedMarkdown(nextBlockquote.children, context, depth + 1),
					isOpenByDefault: false,
				} as AccordionNode);
				i++; // Skip the fused blockquote
				continue;
			}
		}

		switch (node.type) {
			case NodeType.Blockquote: {
				result.push(processBlockquoteForAccordion(node as BlockquoteNode, context, depth));
				break;
			}
			case NodeType.Text: {
				const textNode = node as TextNode;
				// First check for accordions inside text (<details> or >! ... | ... !<)
				const accordionNodes = splitTextNodeForAccordions(textNode, context, depth);
				for (const accNode of accordionNodes) {
					if (accNode.type === NodeType.Text) {
						// Then check for math ($math$ and $$math$$) inside text
						result.push(...splitTextNodeForMath(accNode as TextNode));
					} else {
						result.push(accNode);
					}
				}
				break;
			}
			case NodeType.Strong:
			case NodeType.Emphasis:
			case NodeType.Underline:
			case NodeType.Strikethrough:
			case NodeType.Spoiler:
			case NodeType.Sequence: {
				const formatting = node as FormattingNode;
				result.push({
					...formatting,
					children: enhanceSuperchargedMarkdown(formatting.children, context, depth),
				});
				break;
			}
			case NodeType.Heading: {
				const heading = node as HeadingNode;
				result.push({
					...heading,
					children: enhanceSuperchargedMarkdown(heading.children, context, depth),
				});
				break;
			}
			case NodeType.Subtext: {
				const subtext = node as SubtextNode;
				result.push({
					...subtext,
					children: enhanceSuperchargedMarkdown(subtext.children, context, depth),
				});
				break;
			}
			case NodeType.List: {
				const list = node as ListNode;
				result.push({
					...list,
					items: list.items.map((item) => ({
						...item,
						children: enhanceSuperchargedMarkdown(item.children, context, depth),
					})),
				});
				break;
			}
			case NodeType.Table: {
				const table = node as TableNode;
				result.push({
					...table,
					header: {
						...table.header,
						cells: table.header.cells.map((cell) => ({
							...cell,
							children: enhanceSuperchargedMarkdown(cell.children, context, depth),
						})),
					},
					rows: table.rows.map((row) => ({
						...row,
						cells: row.cells.map((cell) => ({
							...cell,
							children: enhanceSuperchargedMarkdown(cell.children, context, depth),
						})),
					})),
				});
				break;
			}
			case NodeType.Alert: {
				const alert = node as AlertNode;
				result.push({
					...alert,
					children: enhanceSuperchargedMarkdown(alert.children, context, depth),
				});
				break;
			}
			case NodeType.Accordion: {
				const acc = node as AccordionNode;
				result.push({
					...acc,
					children: enhanceSuperchargedMarkdown(acc.children, context, depth),
				});
				break;
			}
			default:
				result.push(node);
				break;
		}
	}

	return result;
}
